import React, { createContext, useContext, useState, useEffect, useCallback, startTransition } from 'react';
import { AuthService } from './AuthService';
import {
  clearAnalyticsUser,
  setAnalyticsUser,
  trackLogin,
  trackSignUp,
} from './utils/analytics';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log('Refreshing user profile...');
      const currentUser = await AuthService.getCurrentUser();
      console.log('User profile refreshed:', currentUser);
      startTransition(() => {
        setUser(currentUser);
        setIsLoading(false);
      });
      if (currentUser) {
        setAnalyticsUser(currentUser);
      } else {
        clearAnalyticsUser();
      }
      return currentUser;
    } catch (error) {
      console.error('Error in refreshUser:', error);
      startTransition(() => {
        setUser(null);
        setIsLoading(false);
      });
      clearAnalyticsUser();
      return null;
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const timeoutId = setTimeout(() => {
      if (isMounted) {
        console.warn('User profile loading timed out after 15 seconds. Setting isLoading to false.');
        setIsLoading(false);
      }
    }, 15000);

    (async () => {
      try {
        await refreshUser();
      } finally {
        if (isMounted) {
          clearTimeout(timeoutId);
        }
      }
    })();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [refreshUser]);

  const login = async (username, password) => {
    const result = await AuthService.login(username, password);
    if (result.success) {
      await refreshUser();
      trackLogin('email');
    }
    return result;
  };

  const loginWithGoogle = async (idToken) => {
    const result = await AuthService.loginWithGoogle(idToken);
    if (result.success) {
      await refreshUser();
      if (result.isNewUser) {
        trackSignUp('google');
      } else {
        trackLogin('google');
      }
    }
    return result;
  };

  const loginWithApple = async (idToken, name = null) => {
    const result = await AuthService.loginWithApple(idToken, name);
    if (result.success) {
      await refreshUser();
      if (result.isNewUser) {
        trackSignUp('apple');
      } else {
        trackLogin('apple');
      }
    }
    return result;
  };

  const logout = async () => {
    await AuthService.logout();
    clearAnalyticsUser();
    startTransition(() => {
      setUser(null);
    });
  };

  return (
    <AuthContext.Provider value={{ user, setUser, isLoading, refreshUser, login, loginWithGoogle, loginWithApple, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
